import type { LayerSpec } from '@/core/pipeline';
import { buildWorley, worleyDist } from '@/lib/noise';
import { mulberry32 } from '@/lib/rng';
import { type RGB } from '@/lib/color';

type VoronoiParams = {
  sites: number;
  hue: number;
  spread: number;
  edges: boolean;
  edgeColor: string;
};

export const voronoiSpec: LayerSpec = {
  kind: 'voronoi',
  category: 'generator',
  animated: false,
  label: 'Voronoi',
  defaults: {
    sites: 16,
    hue: 200,
    spread: 60,
    edges: true,
    edgeColor: '#0a0a0a',
  } satisfies VoronoiParams,
  schema: {
    sites:     { type: 'int',    label: 'Sites',      min: 2,   max: 256, step: 1, default: 16 },
    hue:       { type: 'number', label: 'Hue',        min: 0,   max: 360, step: 1, default: 200 },
    spread:    { type: 'number', label: 'Spread',     min: 0,   max: 360, step: 1, default: 60 },
    edges:     { type: 'bool',   label: 'Edges',      default: true },
    edgeColor: { type: 'color',  label: 'Edge color', default: '#0a0a0a' },
  },
  run: (_input, params, ctx) => {
    const p = params as VoronoiParams;
    const w = ctx.width, h = ctx.height;
    const count = Math.max(2, Math.floor(p.sites));
    const sites = buildWorley(ctx.seed, count, w, h);

    // Pre-pick a colour per site (deterministic).
    const rng = mulberry32(ctx.seed ^ 0xa5a5);
    const colors: RGB[] = new Array(count);
    for (let i = 0; i < count; i++) {
      const hueOff = (rng() - 0.5) * p.spread;
      const sat = 0.55 + rng() * 0.35;
      const lum = 0.45 + rng() * 0.35;
      colors[i] = hslToRgb(((p.hue + hueOff) + 360) % 360, sat, lum);
    }
    const edge = parseEdge(p.edgeColor);

    const out = new ImageData(w, h);
    const data = out.data;
    let i = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++, i += 4) {
        const r = worleyDist(sites, x, y);
        const isEdge = p.edges && (r.f2 - r.f1) < 1.0;
        const c = isEdge ? edge : (colors[r.idx] as RGB);
        data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
      }
    }
    return out;
  },
};

function parseEdge(hex: string): RGB {
  if (!/^#[0-9a-f]{3,6}$/i.test(hex)) return [10, 10, 10];
  const s = hex.slice(1);
  if (s.length === 3) {
    return [
      parseInt((s[0] as string) + (s[0] as string), 16),
      parseInt((s[1] as string) + (s[1] as string), 16),
      parseInt((s[2] as string) + (s[2] as string), 16),
    ];
  }
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}

function hslToRgb(h: number, s: number, l: number): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (h / 60) % 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if      (hp < 1) { r1 = c; g1 = x; }
  else if (hp < 2) { r1 = x; g1 = c; }
  else if (hp < 3) { g1 = c; b1 = x; }
  else if (hp < 4) { g1 = x; b1 = c; }
  else if (hp < 5) { r1 = x; b1 = c; }
  else             { r1 = c; b1 = x; }
  const m = l - c / 2;
  return [
    Math.round((r1 + m) * 255),
    Math.round((g1 + m) * 255),
    Math.round((b1 + m) * 255),
  ];
}
